<script setup lang="ts">
import { computed, ComputedRef, ref, Ref, watch } from 'vue'
import { DateTime } from 'luxon'
import DateTimePicker from '@/components/DateTimePicker.vue'
import DurationPicker from '@/components/DurationPicker.vue'
import { GeneralChartTimeSetup, SerializedGeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { humanReadableTimestamp } from '@/composables/datetime'
import _ from 'lodash'
import { useDisplay } from 'vuetify'

const model = defineModel<SerializedGeneralChartTimeSetup | null>()

const { mobile } = useDisplay()

const startMode: Ref<'absolute' | 'relative_now' | 'relative_end'> = ref('relative_now')
const endMode: Ref<'absolute' | 'relative_now' | 'relative_start'> = ref('relative_now')

const absoluteStartTime: Ref<DateTime> = ref(DateTime.now().minus({ days: 4 }))
const absoluteEndTime: Ref<DateTime> = ref(DateTime.now())
const relativeStartToNow: Ref<number> = ref(1000 * 60 * 60 * 24 * 4) // 4 days in milliseconds
const relativeEndToNow: Ref<number> = ref(0)
const relativeStartToEnd: Ref<number> = ref(1000 * 60 * 60 * 24 * 4)
const relativeEndToStart: Ref<number> = ref(1000 * 60 * 60 * 24 * 4)

const pickedTimeResolution: Ref<number> = ref(1000 * 60 * 60)
const autoChooseTimeResolution: Ref<boolean> = ref(true)
const fixTimeResolution: Ref<boolean> = ref(false)
const enableSynchronization: Ref<boolean> = ref(false)
const synchronizationKey: Ref<string> = ref('')

const canChooseRelativeToEndForStart: ComputedRef<boolean> = computed(() => {
  return endMode.value !== 'relative_start'
})
const canChooseRelativeToStartForEnd: ComputedRef<boolean> = computed(() => {
  return startMode.value !== 'relative_end'
})

const serialized: ComputedRef<SerializedGeneralChartTimeSetup> = computed(() => {
  const result = { synchronized: enableSynchronization.value }

  if (enableSynchronization.value) {
    Object.assign(result, { synchronizationKey: synchronizationKey.value })
  } else {
    Object.assign(result, { synchronizationKey: null })
  }

  if (startMode.value === 'absolute') {
    Object.assign(result, { absoluteStart: absoluteStartTime.value.toMillis(), startRelativeTo: null })
  }
  if (endMode.value === 'absolute') {
    Object.assign(result, { absoluteEnd: absoluteEndTime.value.toMillis(), endRelativeTo: null })
  }
  if (startMode.value === 'relative_now') {
    Object.assign(result, { absoluteStart: null, startRelativeTo: 'now', relativeStart: relativeStartToNow.value })
  }
  if (endMode.value === 'relative_now') {
    Object.assign(result, { absoluteEnd: null, endRelativeTo: 'now', relativeEnd: relativeEndToNow.value })
  }
  if (startMode.value === 'relative_end') {
    Object.assign(result, { absoluteStart: null, startRelativeTo: 'end', relativeStart: relativeStartToEnd.value })
  }
  if (endMode.value === 'relative_start') {
    Object.assign(result, { absoluteEnd: null, endRelativeTo: 'start', relativeEnd: relativeEndToStart.value })
  }

  if (autoChooseTimeResolution.value) Object.assign(result, { fixedTimeResolution: null, timeResolution: null })

  if (!autoChooseTimeResolution.value) {
    if (fixTimeResolution.value) {
      Object.assign(result, { fixedTimeResolution: pickedTimeResolution.value, timeResolution: null })
    } else {
      Object.assign(result, { fixedTimeResolution: null, timeResolution: pickedTimeResolution.value })
    }
  }

  return result as SerializedGeneralChartTimeSetup
})

const chartTimeSetup: ComputedRef<GeneralChartTimeSetup> = computed(() => {
  return GeneralChartTimeSetup.fromSerialized(serialized.value)
})

const maxStartTimestamp = computed(() => {
  if (endMode.value === 'absolute') return absoluteEndTime.value
  if (endMode.value === 'relative_now') return DateTime.now().minus({ milliseconds: relativeEndToNow.value })
  return null
})

const minEndTimestamp = computed(() => {
  if (startMode.value === 'absolute') return absoluteStartTime.value
  if (startMode.value === 'relative_now') return DateTime.now().minus({ milliseconds: relativeStartToNow.value })
  return null
})

watch(
  model,
  () => {
    if (_.isEqual(model.value, serialized.value)) return
    if (!model.value) return

    startMode.value = model.value.absoluteStart !== null ? 'absolute' : model.value.startRelativeTo === 'now' ? 'relative_now' : 'relative_end'
    endMode.value = model.value.absoluteEnd !== null ? 'absolute' : model.value.endRelativeTo === 'now' ? 'relative_now' : 'relative_start'
    if (startMode.value === 'absolute') absoluteStartTime.value = DateTime.fromMillis(model.value.absoluteStart!)
    if (endMode.value === 'absolute') absoluteEndTime.value = DateTime.fromMillis(model.value.absoluteEnd!)
    if (startMode.value === 'relative_now') relativeStartToNow.value = model.value.relativeStart!
    if (endMode.value === 'relative_now') relativeEndToNow.value = model.value.relativeEnd!
    if (startMode.value === 'relative_end') relativeStartToEnd.value = model.value.relativeStart!
    if (endMode.value === 'relative_start') relativeEndToStart.value = model.value.relativeEnd!
    synchronizationKey.value = model.value.synchronizationKey ?? ''
    enableSynchronization.value = model.value.synchronized === true
    if (model.value.timeResolution !== null || model.value.fixedTimeResolution !== null) {
      autoChooseTimeResolution.value = false
      if (model.value.fixedTimeResolution !== null) {
        fixTimeResolution.value = true
        pickedTimeResolution.value = model.value.fixedTimeResolution
        return
      }

      fixTimeResolution.value = false
      pickedTimeResolution.value = model.value.timeResolution!
      return
    }

    autoChooseTimeResolution.value = true
  },
  { deep: true, immediate: true }
)
watch(
  serialized,
  () => {
    if (_.isEqual(model.value, serialized.value)) return

    model.value = serialized.value
  },
  { deep: true, immediate: true }
)
</script>

<template>
  <v-layout-card
    class="pa-0"
    variant="flat"
    elevation="0"
  >
    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-calendar-range"
        start
      />
      {{ $t('chart_sharing.creator.customize.time_setup.title') }}
    </v-card-title>

    <v-card-text
      class="py-2 d-flex flex-column ga-4"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <div class="d-flex flex-column ga-2">
        <!-- start time -->
        <div>
          <div class="text-subtitle-1 font-weight-bold">
            <v-icon
              icon="mdi-clock-start"
              start
            />
            {{ $t('chart_sharing.creator.customize.time_setup.start.title') }}
          </div>
          <v-tabs
            v-model="startMode"
            grow
            color="primary"
            slider-color="primary"
          >
            <v-tab
              value="absolute"
              class="border-b rounded-b-0"
            >
              {{ $t('chart_sharing.creator.customize.time_setup.absolute') }}
            </v-tab>
            <v-tab
              value="relative_now"
              class="border-b rounded-b-0"
            >
              {{ $t('chart_sharing.creator.customize.time_setup.relative_to_now') }}
            </v-tab>
            <v-tab
              value="relative_end"
              class="border-b rounded-b-0"
              :disabled="!canChooseRelativeToEndForStart"
            >
              {{ $t('chart_sharing.creator.customize.time_setup.relative_to_end') }}
            </v-tab>
          </v-tabs>
          <v-tabs-window
            id="start-mode-tabs-window"
            v-model="startMode"
          >
            <v-tabs-window-item value="absolute">
              <v-sheet class="ma-4">
                <v-alert
                  class="my-4"
                  type="info"
                  density="compact"
                >
                  {{ $t('chart_sharing.creator.customize.time_setup.start.absolute_info') }}
                </v-alert>
                <DateTimePicker
                  v-model="absoluteStartTime"
                  :max="maxStartTimestamp"
                />
              </v-sheet>
            </v-tabs-window-item>
            <v-tabs-window-item value="relative_now">
              <v-sheet class="ma-4">
                <v-alert
                  class="my-4"
                  type="info"
                  density="compact"
                >
                  {{ $t('chart_sharing.creator.customize.time_setup.start.relative_to_now_info') }}
                </v-alert>

                <DurationPicker v-model="relativeStartToNow" />
              </v-sheet>
            </v-tabs-window-item>
            <v-tabs-window-item value="relative_end">
              <v-sheet class="ma-4">
                <v-alert
                  class="my-4"
                  type="info"
                  density="compact"
                >
                  {{ $t('chart_sharing.creator.customize.time_setup.start.relative_to_end_info') }}
                </v-alert>

                <DurationPicker v-model="relativeStartToEnd" />
              </v-sheet>
            </v-tabs-window-item>
          </v-tabs-window>
        </div>

        <!-- end time -->
        <div>
          <div class="text-subtitle-1 font-weight-bold">
            <v-icon
              icon="mdi-clock-end"
              start
            />
            {{ $t('chart_sharing.creator.customize.time_setup.end.title') }}
          </div>
          <v-tabs
            v-model="endMode"
            grow
            color="primary"
            slider-color="primary"
          >
            <v-tab
              value="absolute"
              class="border-b rounded-b-0"
            >
              {{ $t('chart_sharing.creator.customize.time_setup.absolute') }}
            </v-tab>
            <v-tab
              value="relative_now"
              class="border-b rounded-b-0"
            >
              {{ $t('chart_sharing.creator.customize.time_setup.relative_to_now') }}
            </v-tab>
            <v-tab
              value="relative_start"
              class="border-b rounded-b-0"
              :disabled="!canChooseRelativeToStartForEnd"
            >
              {{ $t('chart_sharing.creator.customize.time_setup.relative_to_start') }}
            </v-tab>
          </v-tabs>
          <v-tabs-window
            id="end-mode-tabs-window"
            v-model="endMode"
          >
            <v-tabs-window-item value="absolute">
              <v-sheet class="ma-4">
                <v-alert
                  class="my-4"
                  type="info"
                  density="compact"
                >
                  {{ $t('chart_sharing.creator.customize.time_setup.end.absolute_info') }}
                </v-alert>
                <DateTimePicker
                  v-model="absoluteEndTime"
                  :min="minEndTimestamp"
                />
              </v-sheet>
            </v-tabs-window-item>
            <v-tabs-window-item value="relative_now">
              <v-sheet class="ma-4">
                <v-alert
                  class="my-4"
                  type="info"
                  density="compact"
                >
                  {{ $t('chart_sharing.creator.customize.time_setup.end.relative_to_now_info') }}
                </v-alert>

                <DurationPicker v-model="relativeEndToNow" />
              </v-sheet>
            </v-tabs-window-item>
            <v-tabs-window-item value="relative_start">
              <v-sheet class="ma-4">
                <v-alert
                  class="my-4"
                  type="info"
                  density="compact"
                >
                  {{ $t('chart_sharing.creator.customize.time_setup.end.relative_to_start_info') }}
                </v-alert>

                <DurationPicker v-model="relativeEndToStart" />
              </v-sheet>
            </v-tabs-window-item>
          </v-tabs-window>
        </div>
      </div>

      <div>
        <div class="text-subtitle-1 font-weight-bold">
          <v-icon
            icon="mdi-clock-fast"
            start
          />
          {{ $t('chart_sharing.creator.customize.time_setup.time_resolution.title') }}
        </div>
        <v-switch
          v-model="autoChooseTimeResolution"
          :label="$t('chart_sharing.creator.customize.time_setup.time_resolution.automatically_chose_optimal')"
          hide-details
        />
        <v-fade-transition>
          <v-container
            v-if="!autoChooseTimeResolution"
            class="ma-0 pa-0"
          >
            <DurationPicker v-model="pickedTimeResolution" />
            <v-switch
              v-model="fixTimeResolution"
              :label="$t('chart_sharing.creator.customize.time_setup.time_resolution.fixed_time_resolution')"
              hide-details
            />
          </v-container>
        </v-fade-transition>
      </div>

      <div>
        <div class="text-subtitle-1 font-weight-bold">
          <v-icon
            icon="mdi-sync"
            start
          />
          {{ $t('chart_sharing.creator.customize.synchronization.title') }}
        </div>
        <v-switch
          v-model="enableSynchronization"
          :label="$t('chart_sharing.creator.customize.synchronization.enable_synchronization')"
          hide-details
        />
        <v-fade-transition>
          <v-text-field
            v-if="enableSynchronization"
            v-model="synchronizationKey"
            prepend-inner-icon="mdi-key-wireless"
            :label="$t('chart_sharing.creator.customize.synchronization.synchronization_key')"
            :disabled="!enableSynchronization"
            variant="outlined"
          />
        </v-fade-transition>
      </div>

      <v-alert
        type="info"
        variant="tonal"
      >
        <i18n-t
          keypath="chart_sharing.creator.customize.chart_period"
          tag="p"
        >
          <template #start>
            <v-chip
              variant="tonal"
              color="success"
            >
              {{ humanReadableTimestamp(chartTimeSetup.getComputedStart()) }}
            </v-chip>
          </template>
          <template #end>
            <v-chip
              variant="tonal"
              color="success"
            >
              {{ humanReadableTimestamp(chartTimeSetup.getComputedEnd()) }}
            </v-chip>
          </template>
        </i18n-t>
      </v-alert>
    </v-card-text>
  </v-layout-card>
</template>

<style scoped>
#start-mode-tabs-window .v-sheet,
#end-mode-tabs-window .v-sheet {
  margin: 0rem !important;
  box-shadow: none !important;
}
</style>
